import { usePhone } from '@modules/phone/hooks/use-phone';
import { useDeviceStore } from '@modules/phone/store/device.store';
import { useDialerStore } from '@modules/phone/store/dialer.store';
import { usePhoneStore } from '@modules/phone/store/phone.store';
import { TWILIO_CALL_EVENTS } from '@shared/constants';
import { useEffect } from 'react';
import { useCaptureClientCall } from './use-capture-client-call';
import { TwilioCall } from '@/types/app.types';
import { isLiveCall } from './use-device-events';
import { useSwitchCallStore as switchCallStore } from '@shared/layouts/app-layout/components/app-banner/banners/store/use-switch-call.store';

export function useCallEvents() {
  const { openPhoneInCallMode } = usePhoneStore();
  const { closePhone } = usePhone();
  const { call, setCallState, updateAcceptedTimestamp } = useDeviceStore();
  const { openCalls } = useDialerStore();
  const { captureCallInfo } = useCaptureClientCall();

  function onAccept(incomingCall: TwilioCall) {
    const sid = incomingCall?.parameters?.CallSid;
    captureCallInfo(sid);

    // prevent opening dialer on live call
    if (isLiveCall(incomingCall)) {
      return;
    }

    openPhoneInCallMode();
    updateAcceptedTimestamp(new Date().toISOString());
    openCalls();
  }

  function onDisconnect(incomingCall: TwilioCall) {
    const sid = incomingCall?.parameters?.CallSid;
    const switchStore = switchCallStore.getState();
    captureCallInfo(sid);

    if (switchStore.showSwitchCall) {
      switchCallStore.setState({ showSwitchCall: false });
    }

    setCallState(null);
    closePhone();
  }

  function handleCallEvents() {
    call?.on(TWILIO_CALL_EVENTS.accept, onAccept);
    call?.on(TWILIO_CALL_EVENTS.disconnect, onDisconnect);
    call?.on(TWILIO_CALL_EVENTS.reject, onDisconnect);
    call?.on(TWILIO_CALL_EVENTS.cancel, onDisconnect);

    return handleUnregisterCallEvents;
  }

  function handleUnregisterCallEvents() {
    call?.off(TWILIO_CALL_EVENTS.accept, onAccept);
    call?.off(TWILIO_CALL_EVENTS.disconnect, onDisconnect);
    call?.off(TWILIO_CALL_EVENTS.reject, onDisconnect);
    call?.off(TWILIO_CALL_EVENTS.cancel, onDisconnect);
  }

  useEffect(handleCallEvents, [call]);
}
